import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, resolveAssetUrl } from "@/lib/api";
import { CATEGORIES } from "@/lib/categories";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2, Upload, ArrowLeft, Save, Loader2, ArrowUp, ArrowDown } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", slug: "", category: CATEGORIES[0], status: "live",
  short_summary: "", problem: "", target_users: [],
  why_revenue_impact: "", why_cost_savings: "", why_operational_advantage: "",
  core_features: [], advanced_features: [], differentiators: [],
  workflow_steps: [], use_cases: [],
  demo_url: "", tech_stack: [], screenshots: [], thumbnail_url: "",
  is_featured: false, is_public: true, sort_order: 0, internal_notes: "",
};

const STATUS_OPTS = ["live", "beta", "coming-soon", "maintenance"];

export default function AdminSystemEdit() {
  const { id } = useParams();
  const nav = useNavigate();
  const isNew = id === "new";
  const [form, setForm] = useState(EMPTY);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isNew) return;
    Promise.all([
      api.get(`/admin/systems/${id}`),
      api.get(`/admin/systems/${id}/credentials`),
    ]).then(([s, c]) => {
      setForm({ ...EMPTY, ...s.data });
      setCredentials(c.data);
    }).finally(() => setLoading(false));
  }, [id, isNew]);

  const save = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const { data } = await api.post("/admin/systems", form);
        toast.success("System created");
        nav(`/admin/systems/${data.id}`);
      } else {
        await api.put(`/admin/systems/${id}`, form);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="p-10 text-slate-500">Loading…</div>;

  return (
    <div className="p-6 lg:p-10 max-w-5xl pb-32" data-testid="admin-system-edit">
      <Link to="/admin/systems" className="text-sm text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
        <ArrowLeft className="w-4 h-4" /> Back to systems
      </Link>
      <div className="flex items-center justify-between mt-3 flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">{isNew ? "Create system" : form.name}</h1>
          <p className="text-slate-500 mt-1 text-sm">{isNew ? "Add a new system to the showroom." : `Edit “${form.name}”`}</p>
        </div>
        <Button onClick={save} disabled={saving} className="rounded-full bg-slate-900 hover:bg-slate-800" data-testid="admin-save-system">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save
        </Button>
      </div>

      <Tabs defaultValue="basic" className="mt-6">
        <TabsList className="bg-slate-100 rounded-full p-1 flex flex-wrap h-auto">
          <TabsTrigger value="basic" className="rounded-full" data-testid="tab-basic">Basics</TabsTrigger>
          <TabsTrigger value="content" className="rounded-full" data-testid="tab-content">Content</TabsTrigger>
          <TabsTrigger value="features" className="rounded-full" data-testid="tab-features">Features</TabsTrigger>
          <TabsTrigger value="workflow" className="rounded-full" data-testid="tab-workflow">Workflow & Use cases</TabsTrigger>
          <TabsTrigger value="media" className="rounded-full" data-testid="tab-media">Media</TabsTrigger>
          <TabsTrigger value="demo" className="rounded-full" data-testid="tab-demo">Demo & Tech</TabsTrigger>
          {!isNew && <TabsTrigger value="credentials" className="rounded-full" data-testid="tab-credentials">Credentials</TabsTrigger>}
        </TabsList>

        {/* BASICS */}
        <TabsContent value="basic" className="mt-6">
          <Card className="p-6 rounded-2xl space-y-4">
            <Field label="Name *">
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="field-name" />
            </Field>
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Slug (URL)" hint="Leave blank to auto-generate from name">
                <Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="payroll-management-system" data-testid="field-slug" />
              </Field>
              <Field label="Category">
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger data-testid="field-category"><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="field-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUS_OPTS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Sort order">
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} data-testid="field-sort-order" />
              </Field>
            </div>
            <Field label="Short summary">
              <Textarea rows={3} value={form.short_summary} onChange={(e) => setForm({ ...form, short_summary: e.target.value })} data-testid="field-short-summary" />
            </Field>
            <div className="grid sm:grid-cols-2 gap-4">
              <ToggleRow label="Public" checked={form.is_public} onChange={(v) => setForm({ ...form, is_public: v })} testid="field-public" />
              <ToggleRow label="Featured" checked={form.is_featured} onChange={(v) => setForm({ ...form, is_featured: v })} testid="field-featured" />
            </div>
            <Field label="Internal notes" hint="Only visible to admins">
              <Textarea rows={2} value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })} data-testid="field-internal-notes" />
            </Field>
          </Card>
        </TabsContent>

        {/* CONTENT (positioning + why) */}
        <TabsContent value="content" className="mt-6 space-y-4">
          <Card className="p-6 rounded-2xl space-y-4">
            <div className="text-sm font-semibold uppercase tracking-wider text-slate-500">Positioning</div>
            <Field label="Problem"><Textarea rows={3} value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} data-testid="field-problem" /></Field>
            <ListField label="Target users" items={form.target_users} onChange={(arr) => setForm({ ...form, target_users: arr })} placeholder="e.g. CFO" testid="field-target-users" />
          </Card>
          <Card className="p-6 rounded-2xl space-y-4">
            <div className="text-sm font-semibold uppercase tracking-wider text-slate-500">Why this matters</div>
            <Field label="Revenue impact"><Textarea rows={2} value={form.why_revenue_impact} onChange={(e) => setForm({ ...form, why_revenue_impact: e.target.value })} data-testid="field-why-revenue" /></Field>
            <Field label="Cost savings"><Textarea rows={2} value={form.why_cost_savings} onChange={(e) => setForm({ ...form, why_cost_savings: e.target.value })} data-testid="field-why-cost" /></Field>
            <Field label="Operational advantage"><Textarea rows={2} value={form.why_operational_advantage} onChange={(e) => setForm({ ...form, why_operational_advantage: e.target.value })} data-testid="field-why-operational" /></Field>
          </Card>
        </TabsContent>

        {/* FEATURES */}
        <TabsContent value="features" className="mt-6 space-y-4">
          <Card className="p-6 rounded-2xl">
            <ListField label="Core features" items={form.core_features} onChange={(arr) => setForm({ ...form, core_features: arr })} placeholder="Add a core feature" testid="field-core-features" />
          </Card>
          <Card className="p-6 rounded-2xl">
            <ListField label="Advanced features" items={form.advanced_features} onChange={(arr) => setForm({ ...form, advanced_features: arr })} placeholder="Add an advanced feature" testid="field-advanced-features" />
          </Card>
          <Card className="p-6 rounded-2xl">
            <ListField label="Differentiators" items={form.differentiators} onChange={(arr) => setForm({ ...form, differentiators: arr })} placeholder="What sets it apart" testid="field-differentiators" />
          </Card>
        </TabsContent>

        {/* WORKFLOW + USE CASES */}
        <TabsContent value="workflow" className="mt-6 space-y-4">
          <Card className="p-6 rounded-2xl">
            <ObjectListField
              label="Workflow steps"
              items={form.workflow_steps}
              onChange={(arr) => setForm({ ...form, workflow_steps: arr })}
              fields={[{ key: "title", placeholder: "Step title" }, { key: "description", placeholder: "Description", textarea: true }]}
              testid="field-workflow"
            />
          </Card>
          <Card className="p-6 rounded-2xl">
            <ObjectListField
              label="Use cases"
              items={form.use_cases}
              onChange={(arr) => setForm({ ...form, use_cases: arr })}
              fields={[{ key: "title", placeholder: "Use case title" }, { key: "description", placeholder: "Outcome description", textarea: true }]}
              testid="field-usecases"
            />
          </Card>
        </TabsContent>

        {/* MEDIA */}
        <TabsContent value="media" className="mt-6 space-y-4">
          <Card className="p-6 rounded-2xl">
            <div className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Thumbnail *</div>
            <ImageUploader
              value={form.thumbnail_url}
              onChange={(url) => setForm({ ...form, thumbnail_url: url })}
              testid="thumbnail-uploader"
              single
            />
          </Card>
          <Card className="p-6 rounded-2xl">
            <div className="text-sm font-semibold uppercase tracking-wider text-slate-500 mb-3">Screenshots</div>
            <ImageUploader
              value={form.screenshots}
              onChange={(urls) => setForm({ ...form, screenshots: urls })}
              testid="screenshots-uploader"
              multiple
            />
          </Card>
        </TabsContent>

        {/* DEMO */}
        <TabsContent value="demo" className="mt-6 space-y-4">
          <Card className="p-6 rounded-2xl space-y-4">
            <Field label="Demo URL" hint="Opens in a new tab from the system detail page">
              <Input value={form.demo_url} onChange={(e) => setForm({ ...form, demo_url: e.target.value })} placeholder="https://..." data-testid="field-demo-url" />
            </Field>
            <ListField label="Tech stack" items={form.tech_stack} onChange={(arr) => setForm({ ...form, tech_stack: arr })} placeholder="React, FastAPI, ..." testid="field-tech-stack" />
          </Card>
        </TabsContent>

        {/* CREDENTIALS */}
        {!isNew && (
          <TabsContent value="credentials" className="mt-6">
            <CredentialsEditor systemId={id} credentials={credentials} setCredentials={setCredentials} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      {hint && <div className="text-xs text-slate-500 mt-0.5">{hint}</div>}
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function ToggleRow({ label, checked, onChange, testid }) {
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-200 p-4">
      <div className="font-medium">{label}</div>
      <Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
    </div>
  );
}

function ListField({ label, items, onChange, placeholder, testid }) {
  const [val, setVal] = useState("");
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      <div className="flex gap-2 mt-2">
        <Input value={val} onChange={(e) => setVal(e.target.value)} placeholder={placeholder} data-testid={`${testid}-input`} onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } }
        }} />
        <Button type="button" variant="outline" onClick={() => { if (val.trim()) { onChange([...items, val.trim()]); setVal(""); } }} data-testid={`${testid}-add`}>
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      <ul className="mt-3 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="flex items-center justify-between gap-3 p-2 bg-slate-50 rounded-lg">
            <span className="text-sm text-slate-700">{it}</span>
            <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600" data-testid={`${testid}-remove-${i}`}>
              <Trash2 className="w-4 h-4" />
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function ObjectListField({ label, items, onChange, fields, testid }) {
  const blank = Object.fromEntries(fields.map((f) => [f.key, ""]));
  const [obj, setObj] = useState(blank);
  return (
    <div>
      <Label className="text-slate-700">{label}</Label>
      <div className="grid sm:grid-cols-2 gap-2 mt-2">
        {fields.map((f) => f.textarea ? (
          <Textarea key={f.key} placeholder={f.placeholder} rows={2} value={obj[f.key]} onChange={(e) => setObj({ ...obj, [f.key]: e.target.value })} className="sm:col-span-2" data-testid={`${testid}-input-${f.key}`} />
        ) : (
          <Input key={f.key} placeholder={f.placeholder} value={obj[f.key]} onChange={(e) => setObj({ ...obj, [f.key]: e.target.value })} data-testid={`${testid}-input-${f.key}`} />
        ))}
        <Button
          type="button"
          variant="outline"
          className="sm:col-span-2"
          onClick={() => {
            if (!obj.title?.trim()) return;
            onChange([...items, obj]);
            setObj(blank);
          }}
          data-testid={`${testid}-add`}
        >
          <Plus className="w-4 h-4 mr-1.5" /> Add
        </Button>
      </div>
      <ol className="mt-4 space-y-2">
        {items.map((it, i) => (
          <li key={i} className="p-4 bg-slate-50 rounded-xl border border-slate-100">
            <div className="flex items-center justify-between">
              <div className="font-medium">{i + 1}. {it.title}</div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => {
                  if (i === 0) return;
                  const arr = [...items]; [arr[i-1], arr[i]] = [arr[i], arr[i-1]]; onChange(arr);
                }} className="text-slate-400 hover:text-slate-900 p-1"><ArrowUp className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => {
                  if (i === items.length - 1) return;
                  const arr = [...items]; [arr[i+1], arr[i]] = [arr[i], arr[i+1]]; onChange(arr);
                }} className="text-slate-400 hover:text-slate-900 p-1"><ArrowDown className="w-3.5 h-3.5" /></button>
                <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-slate-400 hover:text-red-600 p-1" data-testid={`${testid}-remove-${i}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            {it.description && <div className="text-sm text-slate-600 mt-1">{it.description}</div>}
          </li>
        ))}
      </ol>
    </div>
  );
}

function ImageUploader({ value, onChange, single, multiple, testid }) {
  const inputRef = useRef();
  const [uploading, setUploading] = useState(false);

  const upload = async (files) => {
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      if (single) onChange(urls[0]);
      else onChange([...(value || []), ...urls]);
      toast.success("Uploaded");
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple={multiple}
        className="hidden"
        onChange={(e) => upload(Array.from(e.target.files || []))}
        data-testid={`${testid}-input`}
      />
      <div className="flex flex-wrap gap-3">
        {single && value && (
          <div className="relative w-40 aspect-[4/3] rounded-xl overflow-hidden border border-slate-200">
            <img src={resolveAssetUrl(value)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => onChange("")} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1" data-testid={`${testid}-remove`}><Trash2 className="w-3 h-3" /></button>
          </div>
        )}
        {multiple && (value || []).map((u, i) => (
          <div key={i} className="relative w-40 aspect-[4/3] rounded-xl overflow-hidden border border-slate-200">
            <img src={resolveAssetUrl(u)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => onChange(value.filter((_, idx) => idx !== i))} className="absolute top-1 right-1 bg-red-600 text-white rounded-full p-1" data-testid={`${testid}-remove-${i}`}><Trash2 className="w-3 h-3" /></button>
          </div>
        ))}
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="w-40 aspect-[4/3] rounded-xl border-2 border-dashed border-slate-300 hover:border-slate-500 grid place-items-center text-slate-500 hover:text-slate-700 transition-colors"
          data-testid={`${testid}-button`}
        >
          {uploading ? <Loader2 className="w-5 h-5 animate-spin" /> : <div className="text-center"><Upload className="w-5 h-5 mx-auto" /><div className="text-xs mt-1">Upload</div></div>}
        </button>
      </div>
    </div>
  );
}

function CredentialsEditor({ systemId, credentials, setCredentials }) {
  const [draft, setDraft] = useState({ role: "", username: "", password: "", notes: "", status: "active", sort_order: 0 });
  const [editing, setEditing] = useState(null);

  const reload = () => api.get(`/admin/systems/${systemId}/credentials`).then((r) => setCredentials(r.data));

  const add = async () => {
    if (!draft.role || !draft.username) { toast.error("Role + username required"); return; }
    await api.post(`/admin/systems/${systemId}/credentials`, draft);
    setDraft({ role: "", username: "", password: "", notes: "", status: "active", sort_order: 0 });
    reload();
    toast.success("Credential added");
  };

  const update = async (c) => {
    await api.put(`/admin/credentials/${c.id}`, c);
    setEditing(null);
    reload();
    toast.success("Updated");
  };

  const remove = async (id) => {
    if (!confirm("Delete credential?")) return;
    await api.delete(`/admin/credentials/${id}`);
    reload();
  };

  return (
    <div className="space-y-4" data-testid="credentials-editor">
      <Card className="p-6 rounded-2xl">
        <div className="text-sm font-semibold uppercase tracking-wider text-slate-500">Add credential</div>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <Input placeholder="Role (e.g. Admin)" value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} data-testid="cred-draft-role" />
          <Input placeholder="Username / email" value={draft.username} onChange={(e) => setDraft({ ...draft, username: e.target.value })} data-testid="cred-draft-username" />
          <Input placeholder="Password" value={draft.password} onChange={(e) => setDraft({ ...draft, password: e.target.value })} data-testid="cred-draft-password" />
          <Select value={draft.status} onValueChange={(v) => setDraft({ ...draft, status: v })}>
            <SelectTrigger data-testid="cred-draft-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="inactive">Inactive</SelectItem>
            </SelectContent>
          </Select>
          <Textarea placeholder="Notes (shown under credential)" rows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} className="sm:col-span-2" data-testid="cred-draft-notes" />
        </div>
        <Button onClick={add} className="mt-4 rounded-full bg-slate-900 hover:bg-slate-800" data-testid="cred-add-button">
          <Plus className="w-4 h-4 mr-1.5" /> Add credential
        </Button>
      </Card>

      <div className="space-y-3">
        {credentials.map((c) => (
          <Card key={c.id} className="p-5 rounded-2xl" data-testid={`cred-row-${c.id}`}>
            {editing === c.id ? (
              <div className="grid sm:grid-cols-2 gap-3">
                <Input value={c.role} onChange={(e) => setCredentials((cs) => cs.map((x) => x.id === c.id ? { ...x, role: e.target.value } : x))} />
                <Input value={c.username} onChange={(e) => setCredentials((cs) => cs.map((x) => x.id === c.id ? { ...x, username: e.target.value } : x))} />
                <Input value={c.password} onChange={(e) => setCredentials((cs) => cs.map((x) => x.id === c.id ? { ...x, password: e.target.value } : x))} />
                <Select value={c.status} onValueChange={(v) => setCredentials((cs) => cs.map((x) => x.id === c.id ? { ...x, status: v } : x))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea value={c.notes} onChange={(e) => setCredentials((cs) => cs.map((x) => x.id === c.id ? { ...x, notes: e.target.value } : x))} className="sm:col-span-2" rows={2} />
                <div className="sm:col-span-2 flex gap-2">
                  <Button onClick={() => update(c)} className="rounded-full bg-slate-900 hover:bg-slate-800">Save</Button>
                  <Button variant="outline" onClick={() => setEditing(null)} className="rounded-full">Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs uppercase tracking-wider font-semibold text-slate-500">{c.role}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${c.status === "active" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{c.status}</span>
                  </div>
                  <div className="mt-1 font-mono text-sm text-slate-800 break-all">{c.username}</div>
                  <div className="font-mono text-sm text-slate-500 break-all">{c.password}</div>
                  {c.notes && <div className="text-xs text-slate-500 mt-2">{c.notes}</div>}
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditing(c.id)} data-testid={`cred-edit-${c.id}`}>Edit</Button>
                  <Button variant="outline" size="sm" onClick={() => update({ ...c, status: c.status === "active" ? "inactive" : "active" })} data-testid={`cred-toggle-${c.id}`}>
                    {c.status === "active" ? "Disable" : "Enable"}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => remove(c.id)} data-testid={`cred-delete-${c.id}`}>
                    <Trash2 className="w-4 h-4 text-red-600" />
                  </Button>
                </div>
              </div>
            )}
          </Card>
        ))}
        {credentials.length === 0 && (
          <div className="text-sm text-slate-500 text-center py-8 bg-slate-50 rounded-2xl">No credentials yet.</div>
        )}
      </div>
    </div>
  );
}
